/**
 * kbStatoLettura — letto / non letto / preso visione, ed evidenza nel tempo.
 *
 * Modulo PURO e CONDIVISO: il badge in sidebar, il widget in home e la pagina
 * delle novità devono contare le stesse cose. Se divergessero, il badge
 * mostrerebbe «2 non lette» su una pagina che ne elenca 5 — e un badge di cui non
 * si può fidare è peggio di nessun badge.
 *
 * ⚠️ NESSUN CONTATORE DENORMALIZZATO. La verità sono le righe di
 * `kb_novita_stato`; i volumi sono decine. Un contatore che deriva da quelle
 * righe avrebbe drift garantito e nessuno saprebbe quale dei due credere.
 *
 * Doc canonico: docs/01-ARCHITECTURE/KB-CONTENT-MODEL.md
 */

import { aMillis } from './kbTypes';
import type { KbAnnuncio, KbContenuto, KbIstanteLike, KbStatoLetturaNovita } from './kbTypes';

/** Separatore del doc id: `{user_id}__{content_id}`. Doppio underscore, non singolo. */
export const KB_SEPARATORE_STATO = '__';

/**
 * Doc id dello stato di lettura.
 *
 * L'uid va in TESTA di proposito: la rule fa un match di prefisso pieno
 * (`uid + '__.*'`), non l'ambiguo `.*_uid.*` usato altrove — che con id
 * contenenti underscore può combaciare per caso.
 */
export function idStatoLettura(userId: string, contentId: string): string {
  if (!userId || !contentId) {
    throw new Error('idStatoLettura richiede userId e contentId');
  }
  return `${userId}${KB_SEPARATORE_STATO}${contentId}`;
}

/** Estrae le due parti da un doc id, o `null` se la forma non è quella attesa. */
export function scomponiIdStatoLettura(
  id: string
): { user_id: string; content_id: string } | null {
  const i = id.indexOf(KB_SEPARATORE_STATO);
  if (i <= 0) return null;
  const user_id = id.slice(0, i);
  const content_id = id.slice(i + KB_SEPARATORE_STATO.length);
  if (!user_id || !content_id) return null;
  return { user_id, content_id };
}

// ============================================================================
// LETTO / NON LETTO
// ============================================================================

/** Un annuncio è letto quando esiste una riga con `letto_at`. */
export function eLetto(stato: KbStatoLetturaNovita | undefined | null): boolean {
  return aMillis(stato?.letto_at) != null;
}

/** «Ho capito e confermo» — richiesto dai livelli con `forza_presa_visione`. */
export function haPresoVisione(stato: KbStatoLetturaNovita | undefined | null): boolean {
  return aMillis(stato?.presa_visione_at) != null;
}

/**
 * «L'ho chiuso» ≠ «l'ho letto»: chiudere toglie l'annuncio dall'evidenza del
 * widget ma non conta come lettura, e non lo toglie dall'archivio.
 */
export function eChiuso(stato: KbStatoLetturaNovita | undefined | null): boolean {
  return aMillis(stato?.chiuso_at) != null;
}

/** Indicizza le righe di stato per `content_id`, tenendo la più informativa. */
export function indicizzaStati(
  stati: readonly KbStatoLetturaNovita[]
): Map<string, KbStatoLetturaNovita> {
  const mappa = new Map<string, KbStatoLetturaNovita>();
  for (const s of stati) {
    const esistente = mappa.get(s.content_id);
    if (!esistente) {
      mappa.set(s.content_id, s);
      continue;
    }
    // Se per qualche ragione esistono due righe, vince quella che dice di più:
    // presa visione > letto > chiuso. Non si perde un «ho capito» già dato.
    const punteggio = (x: KbStatoLetturaNovita) =>
      (haPresoVisione(x) ? 4 : 0) + (eLetto(x) ? 2 : 0) + (eChiuso(x) ? 1 : 0);
    if (punteggio(s) > punteggio(esistente)) mappa.set(s.content_id, s);
  }
  return mappa;
}

// ============================================================================
// EVIDENZA NEL TEMPO
// ============================================================================

/**
 * L'annuncio è in evidenza adesso?
 *
 * Fuori evidenza NON significa nascosto: resta nell'archivio delle novità. Serve
 * solo a impedire che il widget si riempia di storia morta e che il badge
 * lampeggi per cose vecchie.
 */
export function inEvidenza(
  annuncio: Pick<KbAnnuncio, 'data_annuncio' | 'scadenza_evidenza_at'> | undefined | null,
  adessoMs: number
): boolean {
  if (!annuncio) return false;
  const inizio = aMillis(annuncio.data_annuncio);
  if (inizio != null && inizio > adessoMs) return false; // programmato nel futuro
  const fine = aMillis(annuncio.scadenza_evidenza_at);
  if (fine != null && fine <= adessoMs) return false;
  return true;
}

/** Data di scadenza dell'evidenza, dato il numero di giorni del livello. */
export function calcolaScadenzaEvidenza(
  dataAnnuncio: KbIstanteLike,
  giorniEvidenza: number
): number | null {
  const inizio = aMillis(dataAnnuncio);
  if (inizio == null) return null;
  const giorni = Number.isFinite(giorniEvidenza) && giorniEvidenza > 0 ? giorniEvidenza : 30;
  return inizio + giorni * 24 * 60 * 60 * 1000;
}

// ============================================================================
// COMPOSIZIONE PER IL WIDGET
// ============================================================================

export interface KbNovitaPerLettore {
  contenuto: KbContenuto;
  letto: boolean;
  presa_visione: boolean;
  chiuso: boolean;
  in_evidenza: boolean;
  /** Capability che il tenant non ha: la UI ci scrive una nota informativa. */
  capability_mancante: string | null;
}

/**
 * Unisce annunci e stato di lettura in ciò che serve alla UI.
 *
 * Il conteggio dei NON LETTI considera solo gli annunci in evidenza: un badge che
 * conta anche l'archivio sarebbe sempre acceso, e un badge sempre accesso non è
 * un segnale.
 */
export function componiNovita(
  visibili: ReadonlyArray<{ contenuto: KbContenuto; capability_mancante: string | null }>,
  stati: readonly KbStatoLetturaNovita[],
  adessoMs: number
): { novita: KbNovitaPerLettore[]; non_letti: number } {
  const perContenuto = indicizzaStati(stati);
  const novita: KbNovitaPerLettore[] = [];
  let non_letti = 0;

  for (const v of visibili) {
    const stato = perContenuto.get(v.contenuto.id);
    const evidenza = inEvidenza(v.contenuto.annuncio, adessoMs);
    const letto = eLetto(stato);
    if (evidenza && !letto) non_letti += 1;
    novita.push({
      contenuto: v.contenuto,
      letto,
      presa_visione: haPresoVisione(stato),
      chiuso: eChiuso(stato),
      in_evidenza: evidenza,
      capability_mancante: v.capability_mancante,
    });
  }

  return { novita, non_letti };
}
